import * as THREE from 'three';
import React from 'react';
import { degToRad } from 'three/src/math/MathUtils';
import MathUtils from './utilities/MathUtils';
import QuizManager from './QuizManager';
import AssetUtility from './AssetUtility';
import { SimulationStates, PrimitiveTypes, QuizType, PublishTypes, MimeToFileExtension } from '../globals';
import store, { StoreActions } from '../state/store';
import ModalManager from '../components/layout/ModalManager';
import Dialog from '../components/layout/Dialog';
import DiscardTemplateChanges from '../actions/ui/DiscardTemplateChanges';
import { INamedObject } from '../interfaces/Misc';

type PrimitiveGeometry = THREE.BoxGeometry | THREE.SphereGeometry | THREE.PlaneGeometry | THREE.CylinderGeometry;

/**
 * a container for general-purpose, widely-used helper functions that don't strictly belong to any one other class or component
 * @export
 * @class Utils
 */
export default class Utils
{
	/**
	 * shorthand to check if the application is in simulation mode
	 * @static
	 * @returns true if the application is play is currently playing in simulation mode, or false otherwise (including pause simulation)
	 * @memberof Utils
	 */
	static IsSimulationRunning()
	{
		return (store.getState().applicationModule.simulationState === SimulationStates.Playing);
	}

	/**
	 * Compare two software version numbers (e.g. 1.7.1)
	 *
	 *  - "Unit tests": http://jsfiddle.net/ripper234/Xv9WL/28/
	 *  - Taken from http://stackoverflow.com/a/6832721/11236
	 * @static
	 * @param {String} v1
	 * @param {String} v2
	 * @returns
	 * - 0 if they're identical
	 * - negative if v1 < v2
	 * - positive if v1 > v2
	 * - Nan if they in the wrong format
	 *
	 * E.g.:
	 * - assert(version_number_compare("1.7.1", "1.6.10") > 0);
	 * - assert(version_number_compare("1.7.1", "1.7.10") < 0);
	 * @memberof Utils
	 */
	static compareVersionNumbers(v1, v2)
	{
		// return -1;
		const v1parts = v1.split('.');
		const v2parts = v2.split('.');

		// First, validate both numbers are true version numbers
		function validateParts(parts)
		{
			for (let i = 0; i < parts.length; ++i)
			{
				if (!MathUtils.isPositiveInteger(parts[i]))
				{
					return false;
				}
			}
			return true;
		}
		if (!validateParts(v1parts) || !validateParts(v2parts))
		{
			return NaN;
		}

		for (let i = 0; i < v1parts.length; ++i)
		{
			if (v2parts.length === i)
			{
				return 1;
			}

			if (v1parts[i] === v2parts[i])
			{
				continue;
			}
			if (v1parts[i] > v2parts[i])
			{
				return 1;
			}
			return -1;
		}

		if (v1parts.length !== v2parts.length)
		{
			return -1;
		}

		return 0;
	}

	/**
	 * compares the suggestedName with each member of the elemenmtCollection to find a unique name
	 *
	 * if the suggestedName already exists on any member of elementCollection, suggestedName will be modified with an incrementing counter for uniqueness
	 *
	 * used for cases where a unique name must be given to a member of a collection of elements, such as new steps or sequences
	 * @static
	 * @param {[*]} elementCollection the collection of existing elements, each with a 'name' property, that will be used to devise a new unique name
	 * @param {String} suggestedName the initial name suggestion, that will be made unique if needed
	 * @returns a new unique element name using an increasing counter format (name, name 1, name 2... name n)
	 * @memberof Utils
	 */
	static getUniqueElementName(elementCollection: INamedObject[], suggestedName: string): string
	{
		const hasName = function (collection: INamedObject[], name: string)
		{
			for (let i = 0; i < collection.length; i++)
			{
				if (collection[i].name === name)
				{
					return true;
				}
			}

			return false;
		};

		if (!hasName(elementCollection, suggestedName))
		{
			return suggestedName;
		}

		let i = 1;
		const idx = suggestedName.lastIndexOf(' ');
		if (idx > 0)
		{
			const existingNumber = Number(suggestedName.substring(idx));
			if (!isNaN(existingNumber))
			{
				i = existingNumber + 1;
				suggestedName = suggestedName.substring(0, idx);
			}
		}

		while (true)
		{
			const newName = suggestedName + " " + i++;

			if (!hasName(elementCollection, newName))
			{
				return newName;
			}
		}
	}

	private static prmitiveGeometryBank: Record<PrimitiveTypes, Record<number, PrimitiveGeometry>> = {
		[PrimitiveTypes.Cube]: {},
		[PrimitiveTypes.Sphere]: {},
		[PrimitiveTypes.Plane]: {},
		[PrimitiveTypes.Cylinder]: {},
	};
	static getPrimitiveGeometry(type: PrimitiveTypes, scale = 1): PrimitiveGeometry
	{
		if (!Utils.prmitiveGeometryBank[type][scale])
		{
			let geometry: any = null;
			switch (type)
			{
				case PrimitiveTypes.Cube:
					geometry = new THREE.BoxGeometry(scale, scale, scale);
					break;
				case PrimitiveTypes.Sphere:
					geometry = new THREE.SphereGeometry(scale * 0.5, 32, 32);
					break;
				case PrimitiveTypes.Plane:
					geometry = new THREE.PlaneGeometry(scale, scale);
					geometry.rotateX(degToRad(-90));
					break;
				case PrimitiveTypes.Cylinder:
					geometry = new THREE.CylinderGeometry(scale * 0.5, scale * 0.5, scale * 2, 32);
					break;
				default:
					throw new Error("Unknown primitive type: " + type);
			}

			//flip all v coordinantes in the geometry's uv
			for (let i = 1; i < geometry.attributes.uv.array.length; i += 2)
			{
				geometry.attributes.uv.array[i] = 1 - geometry.attributes.uv.array[i];
			}
			geometry.attributes.uv.needsUpdate = true;

			Utils.prmitiveGeometryBank[type][scale] = geometry;
		}

		return Utils.prmitiveGeometryBank[type][scale];
	}

	static uiTemplateEditSaveChanges()
	{
		const editingSlide = store.getState().uiModule.editingSlide;
		let backupSlide = null;
		if (editingSlide) backupSlide = store.getState().uiModule.backupSlide;
		if (backupSlide)
		{
			ModalManager.addModal({
				allowClickOut: false,
				content:
					<Dialog data-name={"ui-template-discard-dialog"}
						title={"Save UI Template Changes?"}
						actions={[
							{
								label: "Confirm",
								onClick: function ()
								{
									store.dispatch(StoreActions.ui.setBackupSlide(null));
									store.dispatch(StoreActions.ui.setEditingSlide(""));
								},
							},
							{
								label: "Discard",
								onClick: function ()
								{
									new DiscardTemplateChanges(editingSlide, backupSlide);
									store.dispatch(StoreActions.ui.setBackupSlide(null));
									store.dispatch(StoreActions.ui.setEditingSlide(""));
								},
							},
						]}
					>
					</Dialog>
			});
		}
	}	

	/**
	 * from 'inputFileName.type' will return 'type'
	 * - taken from SO: https://stackoverflow.com/a/12900504/618726
	 * @static
	 * @param {String} fileName any string will do, though this function is meant for use with actual file names.
	 * @returns the file extension of the given file name, excluding the '.' if no dots are present, the whole string will be returned.
	 * @memberof Utils
	 */
	static getFileExtension(fileName: string)
	{
		return fileName.slice((fileName.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
	}

	/**
	 * taken from https://stackoverflow.com/a/4250408/618726
	 *
	 * @static
	 * @param {*} fileName
	 * @returns
	 * @memberof Utils
	 */
	static getFileNameNoExtension(fileName)
	{
		return fileName.replace(/\.[^/.]+$/, "");
	}	

	static openURL(url)
	{
		if (!url.includes(":"))
		{
			url = "http://" + url;
		}
		window.open(url, "_blank");
	}

	static isPublishTypeValid(publishType)
	{
		const result = { valid: true, message: "" };
		const quizType = QuizManager.getQuizType();

		switch (publishType)
		{
			case PublishTypes.persistent_for_any_user:
				result.valid = !quizType ? true : false;
				break;
			case PublishTypes.passive_session:
				result.valid = quizType === QuizType.GRADED ? false : true;
				break;
			default: break;
		}

		return result;
	}
	
	/**
	 * Should return the file extension of the given asset id if it exists  
	 * First it parses the asset name, but if that fails it will attempt to fetch the asset file from the server and determine its file format using content type mapping
	 *
	 * @static
	 * @param {string} assetId
	 * @return {*}  {Promise<string>}
	 * @memberof Utils
	 */
	static async fetchAssetFileExtension(assetId: string): Promise<string>
	{
		const assetName = AssetUtility.getAssetName(assetId);
		if(!assetName) return "";
	
		const assetExtension = Utils.getFileExtension(assetName);
		if(!assetExtension)
		{
			const assetRawUrl = AssetUtility.getRawAssetURL(assetId);
			const response = await fetch(assetRawUrl);
			if(!response.ok) return "";
	
			// the simpler way is to assume the server responds with the correct mime type
			// if we find this fails in the future, we'll have to check the content type ourselves
			// probably by checking the first few bytes of the file and comparing to known file formats
			const contentType = response.headers.get("content-type") || "";
			return MimeToFileExtension[contentType] || "";
		}
	
		return assetExtension;
	}
}